import React from 'react';
import { isMemberOnlineNow } from '../utils/dashboard';

function findActiveMember(members, activeMemberId) {
  if (!activeMemberId) return null;
  return members.find((m) => m.device_unique_id === activeMemberId) || null;
}

/**
 * 地图右上角：网络/电量/刷新状态条（上线公告已移至地图顶部公告栈）
 * @param {'desktop'|'mobile'} variant
 */
export default function DashboardStatusHud({
  variant = 'desktop',
  members = [],
  activeMemberId,
  networkQuality,
  devicePerformance,
  refreshStatus,
  lastRefreshTime,
  onRefresh
}) {
  const compact = variant === 'mobile';
  const chipPad = compact ? '3px 6px' : '4px 8px';
  const chipFont = compact ? 10 : 11;
  const chipGap = compact ? 2 : 4;
  const stackRight = compact ? 10 : 18;
  const stackGap = compact ? 2 : 4;

  const activeMember = findActiveMember(members, activeMemberId);
  const activeOnline = activeMember ? isMemberOnlineNow(activeMember) : false;

  let netBg = '#f44336';
  let netIcon = '🔴';
  let netText = compact ? '较差' : '网络较差';
  if (activeMemberId) {
    if (!activeMember) {
      netBg = '#9e9e9e';
      netIcon = '❓';
      netText = compact ? '未找到' : '队员未找到';
    } else if (activeOnline) {
      netBg = '#4caf50';
      netIcon = '🟢';
      netText = compact ? '在线' : '队员在线';
    } else {
      netBg = '#9e9e9e';
      netIcon = '⚫';
      netText = compact ? '离线' : '队员离线';
    }
  } else if (networkQuality === 'good') {
    netBg = '#4caf50';
    netIcon = '🟢';
    netText = compact ? '良好' : '网络良好';
  } else if (networkQuality === 'fair') {
    netBg = '#ff9800';
    netIcon = '🟡';
    netText = compact ? '一般' : '网络一般';
  }

  const batteryLevel = activeMember?.battery_level;
  const showBattery = Boolean(activeMemberId && activeMember && activeOnline && batteryLevel != null);
  const hasValidLocation = Boolean(
    activeMember?.last_latitude && activeMember?.last_longitude
  );

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 10,
          right: stackRight,
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          gap: stackGap
        }}
      >
        <div
          style={{
            padding: chipPad,
            borderRadius: 4,
            fontSize: chipFont,
            background: netBg,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: chipGap
          }}
        >
          <span>{netIcon}</span>
          <span>{netText}</span>
        </div>

        {activeMemberId ? (
          showBattery ? (
            <div
              style={{
                padding: chipPad,
                borderRadius: 4,
                fontSize: chipFont,
                background:
                  batteryLevel >= 50 ? '#4caf50' : batteryLevel >= 20 ? '#ff9800' : '#f44336',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: chipGap
              }}
            >
              <span>🔋</span>
              <span>{compact ? `${batteryLevel}%` : `电量${batteryLevel}%`}</span>
            </div>
          ) : null
        ) : (
          <div
            style={{
              padding: chipPad,
              borderRadius: 4,
              fontSize: chipFont,
              background:
                devicePerformance === 'high'
                  ? '#4caf50'
                  : devicePerformance === 'medium'
                    ? '#ff9800'
                    : '#f44336',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: chipGap
            }}
          >
            <span>
              {devicePerformance === 'high' ? '⚡' : devicePerformance === 'medium' ? '⚙️' : '🐌'}
            </span>
            <span>
              {devicePerformance === 'high'
                ? '高性能'
                : devicePerformance === 'medium'
                  ? compact
                    ? '中等'
                    : '中等性能'
                  : '低性能'}
            </span>
          </div>
        )}

        {activeMemberId ? (
          activeMember && activeOnline ? (
            <div
              style={{
                padding: chipPad,
                borderRadius: 4,
                fontSize: chipFont,
                background: hasValidLocation ? '#4caf50' : '#f44336',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: chipGap
              }}
            >
              <span>{hasValidLocation ? '📍' : '❌'}</span>
              <span>
                {hasValidLocation
                  ? compact
                    ? '有效'
                    : '位置有效'
                  : compact
                    ? '无效'
                    : '位置无效'}
              </span>
            </div>
          ) : null
        ) : (
          <div
            style={{
              padding: chipPad,
              borderRadius: 4,
              fontSize: chipFont,
              background:
                refreshStatus === 'idle'
                  ? '#4caf50'
                  : refreshStatus === 'loading'
                    ? '#ff9800'
                    : '#f44336',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: chipGap
            }}
          >
            <span>
              {refreshStatus === 'idle' ? '✅' : refreshStatus === 'loading' ? '⏳' : '❌'}
            </span>
            <span>
              {refreshStatus === 'idle'
                ? '正常'
                : refreshStatus === 'loading'
                  ? '刷新中'
                  : compact
                    ? '失败'
                    : '刷新失败'}
            </span>
          </div>
        )}

        <button
          onClick={() => {
            console.log('手动刷新设备列表');
            onRefresh?.();
          }}
          disabled={refreshStatus === 'loading'}
          title={
            lastRefreshTime
              ? `最后刷新: ${lastRefreshTime.toLocaleTimeString()}`
              : '刷新设备列表'
          }
          style={{
            padding: chipPad,
            borderRadius: 4,
            fontSize: chipFont,
            background: refreshStatus === 'loading' ? '#ccc' : '#1976d2',
            color: '#fff',
            border: 'none',
            cursor: refreshStatus === 'loading' ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: chipGap
          }}
        >
          <span>🔄</span>
          <span>刷新</span>
        </button>
      </div>
    </>
  );
}
